/**
 * The in-flight `ESC` menu (G38) — *"YOUR MISSION IS NOT COMPLETE.  DO YOU WISH
 * TO:"* — as a `ShellDialog`.
 *
 * **The original's, down to the double spaces.** Every string here is verbatim
 * from `RB.EXE`'s own pool; the sentences run two spaces after a full stop
 * because the game's do, and a lane that "tidies" them is changing a quotation.
 * The capture is `docs/game/ui-reference/dialog-mission-not-complete.png`, and
 * it settles the row set: **three buttons, not five.** `ABORT MISSION` and
 * `END MISSION` belong to the sibling *"YOUR MISSION IS OVER."* board, which the
 * port cannot reach — a decided mission freezes its runner and goes straight to
 * the debriefing, so there is no state in which this port would draw it.
 *
 * ## What is measured and what is ours
 *
 * The board's own geometry is measured off that capture: **196 logical pixels
 * wide**, which is already `popup-chrome.ts`'s `POPUP_MAX_WIDTH` because that
 * constant was derived from this very file. `ESC_MENU_WIDTH` asks for it
 * explicitly so this one dialog is the original's width rather than merely
 * capped by it.
 *
 * Everything else rides the shell's existing popup chrome, and that is a
 * deliberate inheritance rather than an oversight: `popup-chrome.ts` states the
 * project's standing decision that *"the style is the original's, the box is
 * ours"*. The capture's board differs from the port's popup in row height (18
 * against 9) and row width (116 centred, against the full board less padding),
 * and matching those would mean a second popup vocabulary for one screen. The
 * departure is recorded rather than chased.
 *
 * ## The three guards
 *
 * `RB.EXE` carries three refusals beside the dialog, and the manual's *Quitting*
 * section is the rule they enforce: you may quit provided the aircraft is not
 * fatally damaged and no enemy is near. They are modelled as *reasons the board
 * refuses*, not as missing rows, because that is what the strings are — full
 * sentences, shown instead of the choice.
 */

import { dialogRow, type ShellDialog } from "./shell-screen.ts";

/** `RB.EXE`, verbatim. Two spaces after the stop, as the original has it. */
export const ESC_MENU_PROMPT = "YOUR MISSION IS NOT COMPLETE.  DO YOU WISH TO:";

/** The three guard sentences, `RB.EXE` verbatim. */
export const ESC_GUARD_IN_COMBAT = "YOU'RE IN THE MIDST OF COMBAT!  YOU CAN'T QUIT NOW!!";
export const ESC_GUARD_DAMAGED = "YOUR PLANE IS SERIOUSLY DAMAGED!  YOU'LL HAVE TO FINISH THE MISSION ON YOUR OWN.";
export const ESC_GUARD_CAPTURED = "YOU'VE BEEN CAPTURED BY THE ENEMY!";

/** The measured width of the original's own board, off the capture. `popup-chrome.ts`'s ceiling is the same number, and for the same reason. */
export const ESC_MENU_WIDTH = 196;

/**
 * `SERIOUSLY DAMAGED` as a number.
 *
 * The original's own threshold is not recovered — the string is in the pool and
 * nothing decoded says what tests it — so this is a **port-side** figure and is
 * flagged as one. It is not invented from nothing, though: `CombatHud.airframe`
 * is the worst component's health on a 0..1 scale, and a quarter left is the
 * point past which `ai-pilot.ts` already treats an aeroplane as a wreck flying
 * home rather than a combatant. Reusing that boundary is the same move
 * `time-compression.ts` made for its own gate, and for the reason it gives:
 * source a threshold from an existing tunable rather than pick a new one.
 */
export const ESC_SERIOUS_DAMAGE_AIRFRAME = 0.25;

/** Which of the original's three refusals applies, or `undefined` when the board may be shown. */
export type EscMenuRefusal = "combat" | "damaged" | "captured";

export interface EscMenuSituation {
  /**
   * An enemy is close enough that this counts as being in the fight.
   * `time-compression.ts`'s gate answers exactly this question already and is
   * reused rather than re-derived, so the two features can never disagree about
   * what "in combat" means.
   */
  readonly inCombat: boolean;
  /** `CombatHud.airframe` — 0..1 worst component health. */
  readonly airframe: number;
  /** The player is the wrong side of the lines with no aeroplane to leave in. */
  readonly captured: boolean;
  /** PREFERENCES → FLYING → BAIL OUT. */
  readonly bailEnabled: boolean;
  /** `FlightHud.bailAvailable` — the engine's own `pilotMayBailOut`. */
  readonly bailAvailable: boolean;
  /** The running MISSION SCORE, or `undefined` for the bare instant dogfight, which has no mission to score. */
  readonly score: number | undefined;
}

/**
 * The refusal in the original's own precedence: capture first (there is no
 * aeroplane left to make the choice in), then damage, then combat.
 *
 * Ordered that way because they are increasingly recoverable — a captured pilot
 * has no sortie to continue, a badly damaged one has a sortie he must finish
 * alone, and a pilot merely being shot at need only break off.
 */
export function escMenuRefusal(situation: EscMenuSituation): EscMenuRefusal | undefined {
  if (situation.captured) return "captured";
  if (situation.airframe <= ESC_SERIOUS_DAMAGE_AIRFRAME) return "damaged";
  return situation.inCombat ? "combat" : undefined;
}

const REFUSAL_TEXT: Readonly<Record<EscMenuRefusal, string>> = {
  combat: ESC_GUARD_IN_COMBAT,
  damaged: ESC_GUARD_DAMAGED,
  captured: ESC_GUARD_CAPTURED,
};

/**
 * The board itself.
 *
 * A refusal produces the original's sentence over a single `CONTINUE FLYING` —
 * the player is told why and put back in the aeroplane, which is what the
 * strings say happens. Otherwise the capture's three rows, plus BAIL OUT when
 * the pilot has the option.
 *
 * **BAIL OUT is a fourth row the capture does not have**, and it is here on the
 * user's own decision (2026-08-27): the original has no bail-out at all, so its
 * board could not carry one, and AD-021's verb needs a mouse-reachable entry
 * point beside the held `H`. It is the *same command* — `EncounterRunner.bailOut`
 * — with no second copy of the rules and no second hold, because this dialog is
 * already a deliberate, paused context and a confirmation inside a confirmation
 * is noise. Its label is the original's imperative voice, matching the three
 * rows it sits under.
 */
export function escMenuDialog(situation: EscMenuSituation): ShellDialog {
  const refusal = escMenuRefusal(situation);
  if (refusal) {
    return {
      title: "",
      body: [REFUSAL_TEXT[refusal]],
      rows: [dialogRow("continue", "CONTINUE FLYING", { kind: "close" })],
      focus: 0,
      minWidth: ESC_MENU_WIDTH,
    };
  }
  const rows = [
    dialogRow("continue", "CONTINUE FLYING", { kind: "close" }),
    dialogRow("return", "RETURN TO BASE", { kind: "command", command: "esc:return" }),
    dialogRow("restart", "RESTART MISSION", { kind: "command", command: "esc:restart" }),
  ];
  if (situation.bailEnabled && situation.bailAvailable) {
    rows.push(dialogRow("bail", "BAIL OUT", { kind: "command", command: "esc:bail" }));
  }
  return {
    title: "",
    body: [ESC_MENU_PROMPT],
    rows,
    focus: 0,
    minWidth: ESC_MENU_WIDTH,
    footer: situation.score === undefined ? undefined : `SCORE: ${situation.score}`,
  };
}
